import React, { useEffect, useState } from "react";
import { Box, Grid, IconButton, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  ArrowBackOutlined,
  CloseOutlined,
  DeleteOutline,
  TitleSharp,
  UploadOutlined,
} from "@mui/icons-material";
import AdminLayout from "../../../layout/AdminLayout";
import { colors } from "../../../theme/theme";
import { CustomInput } from "../../../components/Common/CustomInput";
import { CustomBtn } from "../../../components/Common/CustomBtn";
import CustomDatePicker from "../../../components/Common/CustomDatePicker";
import {
  ConfirmationSnackbar,
  ErrorSnackbar,
} from "../../../components/Common/CustomSnackbar";
import { dataURItoBlob } from "../../../utils/services/ImageUpload";
import axiosInstance from "../../../utils/config/config";

const AdminEditPost = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const postId = location.state?.postId;

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [date, setDate] = useState(null);
  const [images, setImages] = useState([]);
  const [success, setSuccess] = useState(false);
  const [errorState, setErrorState] = useState({ error: false, errorMsg: "" });

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const { data } = await axiosInstance.get(`/post/getPostById/${postId}`);
        setPost(data);
        setTitle(data?.title || "");
        setContent(data?.content || "");
        setDate(data?.date ? dayjs(data.date) : null);
        setImages(data?.images?.map((img) => img.image_url) || []);
      } catch (error) {
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    if (postId) {
      fetchPost();
    } else {
      setLoading(false);
    }
  }, [postId]);

  const handleAddImages = (files) => {
    Array.from(files).forEach((file) => {
      dataURItoBlob(file, (url) => setImages((prev) => [...prev, url]));
    });
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      await axiosInstance.put(`/post/updatePost/${postId}`, {
        title,
        content,
        date: date?.format ? date.format("YYYY-MM-DD") : date,
        images,
      });
      setSuccess(true);
    } catch (error) {
      setErrorState({
        error: true,
        errorMsg: error?.response?.data?.message || "Failed to update post",
      });
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/post/deletePost/${postId}`);
      navigate("/admin/posts");
    } catch (error) {
      setErrorState({
        error: true,
        errorMsg: error?.response?.data?.message || "Failed to delete post",
      });
    }
  };

  if (loading) return <AdminLayout heading="Edit Post" />;

  if (!post) {
    return (
      <AdminLayout heading="Edit Post">
        <Typography
          sx={{
            color: "rgba(255,255,255,0.6)",
            fontFamily: "Mulish-Regular",
            fontSize: "14px",
          }}
        >
          Post not found.
        </Typography>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      heading="Edit Post"
      actionButton={
        <IconButton onClick={handleDelete}>
          <DeleteOutline sx={{ color: colors.secondary }} />
        </IconButton>
      }
    >
      <ErrorSnackbar
        error={errorState.error}
        handleCloseError={() => setErrorState({ error: false, errorMsg: "" })}
        errorMsg={errorState.errorMsg}
      />
      <ConfirmationSnackbar
        snackbarState={success}
        handleSnackbarClose={() => setSuccess(false)}
        text="Post Updated Successfully"
      />

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => navigate("/admin/posts")}
        sx={{ cursor: "pointer", mb: 2, width: "fit-content" }}
      >
        <ArrowBackOutlined sx={{ color: colors.secondary, fontSize: "18px" }} />
        <Typography
          sx={{
            color: colors.secondary,
            fontFamily: "Mulish-SemiBold",
            fontSize: "14px",
          }}
        >
          Back to Posts
        </Typography>
      </Stack>

      <Grid container spacing={4}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <CustomInput
            label={"Post Name"}
            value={title}
            handleChange={(e) => setTitle(e.target.value)}
            placeholder={"Enter post name"}
            Icon={TitleSharp}
            width={"100%"}
            color={"#fff"}
            borderColor={colors.secondary}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <CustomDatePicker
            label={"Date"}
            value={date}
            setValue={setDate}
            placeholder={"Select date"}
            width={"100%"}
            borderColor={colors.secondary}
            color={"#fff"}
            iconColor={"rgba(255,255,255,0.5)"}
          />
        </Grid>

        <Grid item size={{ xs: 12 }}>
          <CustomInput
            label={"Content"}
            value={content}
            handleChange={(e) => setContent(e.target.value)}
            placeholder={"Enter post content"}
            noIcon
            width={"100%"}
            color={"#fff"}
            borderColor={colors.secondary}
            textArea
          />
        </Grid>

        <Grid item size={{ xs: 12 }}>
          <Typography
            sx={{
              fontFamily: "Mulish-Regular",
              fontSize: "12px",
              color: "#fff",
              fontWeight: "700",
              mb: 0.5,
              textTransform: "uppercase",
            }}
          >
            Images
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {images.map((image, index) => (
              <Box
                key={index}
                sx={{ position: "relative", width: "90px", height: "90px" }}
              >
                <Box
                  component="img"
                  src={image}
                  alt={`upload-${index}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveImage(index)}
                  sx={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    backgroundColor: colors.secondary,
                    "&:hover": { backgroundColor: colors.secondary },
                    p: 0.3,
                  }}
                >
                  <CloseOutlined sx={{ color: "#fff", fontSize: "14px" }} />
                </IconButton>
              </Box>
            ))}
            <Box
              onClick={() =>
                document.getElementById("editPostImagesInput").click()
              }
              sx={{
                width: "90px",
                height: "90px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px dashed #ccc",
                borderRadius: "8px",
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f0f0f0" },
              }}
            >
              <input
                type="file"
                id="editPostImagesInput"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleAddImages(e.target.files)}
              />
              <UploadOutlined sx={{ color: colors.primary }} />
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <CustomBtn text={"Save"} width={"200px"} mt={3} onClick={handleSave} />
    </AdminLayout>
  );
};

export default AdminEditPost;
